import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./phone-input.css";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/components/ThemeContext";

export default function AccountCapture({ onSubmit, onBack, loading }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", business_name: "" });
  const [errors, setErrors] = useState({});
  const { logos, logoStyle } = useTheme();

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "First name is required";
    if (!form.last_name.trim()) errs.last_name = "Last name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Invalid email";
    if (!form.phone || !form.phone.trim()) errs.phone = "Phone number is required";
    else if (!isValidPhoneNumber(form.phone)) errs.phone = "Please enter a valid phone number";
    if (!form.business_name.trim()) errs.business_name = "Business name is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onSubmit(form);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10" style={{ backgroundColor: 'var(--rhz-bg)' }} data-testid="account-capture">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <button
          data-testid="back-to-landing-btn"
          onClick={onBack}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity mb-10 font-['Albert_Sans'] text-sm uppercase tracking-wider"
          style={{ color: 'var(--rhz-text-secondary)' }}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <img src={logos.horizontal} alt="RHZ" className="h-12 mb-8" style={logoStyle} />

        <h2 className="font-['Bruno_Ace_SC'] text-xl sm:text-2xl uppercase tracking-[0.15em] mb-3" style={{ color: 'var(--rhz-text-primary)' }} data-testid="capture-title">
          Before We Begin
        </h2>
        <p className="font-['Albert_Sans'] text-sm mb-10 leading-relaxed" style={{ color: 'var(--rhz-text-secondary)' }}>
          Enter your details below to start the audit. No password required.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-['Albert_Sans'] text-xs uppercase tracking-wider block mb-2" style={{ color: 'var(--rhz-text-secondary)' }}>
                First Name
              </label>
              <Input
                data-testid="first-name-input"
                value={form.first_name}
                onChange={(e) => setForm(p => ({ ...p, first_name: e.target.value }))}
                placeholder="First name"
                className="rounded-sm h-12 font-['Albert_Sans']"
                style={{ backgroundColor: 'var(--rhz-input-bg)', borderColor: 'var(--rhz-border)', color: 'var(--rhz-text-primary)' }}
              />
              {errors.first_name && <p className="text-[#DC0706] text-xs mt-1 font-['Albert_Sans']" data-testid="first-name-error">{errors.first_name}</p>}
            </div>
            <div>
              <label className="font-['Albert_Sans'] text-xs uppercase tracking-wider block mb-2" style={{ color: 'var(--rhz-text-secondary)' }}>
                Last Name
              </label>
              <Input
                data-testid="last-name-input"
                value={form.last_name}
                onChange={(e) => setForm(p => ({ ...p, last_name: e.target.value }))}
                placeholder="Last name"
                className="rounded-sm h-12 font-['Albert_Sans']"
                style={{ backgroundColor: 'var(--rhz-input-bg)', borderColor: 'var(--rhz-border)', color: 'var(--rhz-text-primary)' }}
              />
              {errors.last_name && <p className="text-[#DC0706] text-xs mt-1 font-['Albert_Sans']" data-testid="last-name-error">{errors.last_name}</p>}
            </div>
          </div>

          <div>
            <label className="font-['Albert_Sans'] text-xs uppercase tracking-wider block mb-2" style={{ color: 'var(--rhz-text-secondary)' }}>
              Email
            </label>
            <Input
              data-testid="email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
              placeholder="your@email.com"
              className="rounded-sm h-12 font-['Albert_Sans']"
              style={{ backgroundColor: 'var(--rhz-input-bg)', borderColor: 'var(--rhz-border)', color: 'var(--rhz-text-primary)' }}
            />
            {errors.email && <p className="text-[#DC0706] text-xs mt-1 font-['Albert_Sans']" data-testid="email-error">{errors.email}</p>}
          </div>

          <div>
            <label className="font-['Albert_Sans'] text-xs uppercase tracking-wider block mb-2" style={{ color: 'var(--rhz-text-secondary)' }}>
              Phone Number
            </label>
            <div className="rhz-phone-wrapper" data-testid="phone-input-wrapper">
              <PhoneInput
                international
                defaultCountry="ZA"
                value={form.phone}
                onChange={(v) => setForm(p => ({ ...p, phone: v || "" }))}
                placeholder="Your phone number"
                numberInputProps={{ "data-testid": "phone-input" }}
              />
            </div>
            {errors.phone && <p className="text-[#DC0706] text-xs mt-1 font-['Albert_Sans']" data-testid="phone-error">{errors.phone}</p>}
          </div>

          <div>
            <label className="font-['Albert_Sans'] text-xs uppercase tracking-wider block mb-2" style={{ color: 'var(--rhz-text-secondary)' }}>
              Business Name
            </label>
            <Input
              data-testid="business-name-input"
              value={form.business_name}
              onChange={(e) => setForm(p => ({ ...p, business_name: e.target.value }))}
              placeholder="Your business name"
              className="rounded-sm h-12 font-['Albert_Sans']"
              style={{ backgroundColor: 'var(--rhz-input-bg)', borderColor: 'var(--rhz-border)', color: 'var(--rhz-text-primary)' }}
            />
            {errors.business_name && <p className="text-[#DC0706] text-xs mt-1 font-['Albert_Sans']" data-testid="business-name-error">{errors.business_name}</p>}
          </div>

          <Button
            data-testid="begin-audit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#DC0706] text-white hover:bg-[#FE4D00] transition-all py-6 uppercase tracking-[0.25em] font-['Bruno_Ace_SC'] text-sm rounded-sm mt-4"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                Begin Audit
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
